import { ISearchFields, ISearchToken } from 'types/searchInterfaces'

/**
 * Converts the given data object into an array of search tokens.
 *
 * @param {ISearchFields} data - The data object to convert.
 * @return {ISearchToken[]} An array of search tokens.
 */
export const convertToTokens = (data: ISearchFields): ISearchToken[] => {
  const tokens: ISearchToken[] = []

  if (data.availability.startDate) {
    const token = {
      kind: 'availability',
      required: true,
      start: data.availability.startDate,
      end: data.availability.endDate,
    }

    if (!token.end) {
      token.end = new Date(token.start)
      token.end.setDate(token.end.getDate() + 90)
    }

    tokens.push(token)
  }

  if (data.certificates) {
    for (const cert of data.certificates) {
      if (cert.certificateId) {
        tokens.push({
          kind: 'certificate',
          certificateId: cert.certificateId,
          required: cert.required !== undefined ? cert.required : true,
        })
      }
    }
  }

  if (data.competences) {
    for (const comp of data.competences) {
      if (comp.competenceId) {
        tokens.push({
          kind: 'competence',
          competenceId: comp.competenceId,
          required: comp.required !== undefined ? comp.required : true,
        })
      }
    }
  }

  if (data.degrees) {
    for (const degree of data.degrees) {
      if (degree.degree) {
        tokens.push({
          kind: 'degreeText',
          text: degree.degree,
        })
      }
    }
  }

  if (data.courses) {
    for (const course of data.courses) {
      if (course.course) {
        tokens.push({
          kind: 'courseText',
          text: course.course,
        })
      }
    }
  }

  if (data.location) {
    tokens.push({
      kind: 'locationText',
      text: data.location,
    })
  }

  if (data.projectCountItems) {
    for (const project of data.projectCountItems) {
      const skillIds = project.skills.map(skill => skill.id)
      tokens.push({
        kind: 'projectCount',
        count: project.count,
        skills: skillIds,
      })
    }
  }

  if (data.name) {
    for (let part of data.name.replace(/,/g, ' ').split(/\s+/)) {
      part = part.trim()
      if (part.length > 0) {
        tokens.push({
          kind: 'name',
          value: part,
        })
      }
    }
  }

  if (data.roles) {
    for (const role of data.roles) {
      if (role.skillId) {
        tokens.push({
          kind: 'role',
          skillId: role.skillId,
          minExperience: role.experienceMonths ?? 0,
          minLevel: role.level ?? 0,
          required: role.required !== undefined ? role.required : true,
        })
      }
    }
  }

  if (data.skills) {
    for (const skill of data.skills) {
      if (skill.skillId) {
        tokens.push({
          kind: 'skill',
          skillId: skill.skillId,
          minExperience: skill.experienceMonths ?? 0,
          minLevel: skill.level ?? 0,
          required: skill.required !== undefined ? skill.required : true,
        })
      }
    }
  }

  if (data.industries) {
    for (const industry of data.industries) {
      if (industry.skillId) {
        tokens.push({
          kind: 'industry',
          skillId: industry.skillId,
          minExperience: industry.experienceMonths ?? 0,
          minLevel: industry.level ?? 0,
          required: industry.required !== undefined ? industry.required : true,
        })
      }
    }
  }

  if (data.languages) {
    for (const language of data.languages) {
      if (language.languageCode) {
        tokens.push({
          kind: 'language',
          languageCode: language.languageCode,
          level: language.level,
        })
      }
    }
  }

  return tokens
}
